import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Repeat2, Check } from 'lucide-react';
/*
 * Il punteggio dell'incontro si scrive con `puntiTorneo`, il formatter delle
 * classifiche: sulla scala 2 / 1 / 0 (`MATCH_SCORING`) ogni conto e' un intero,
 * e la copia locale che stava qui — che faceva `punti - interi >= 0.5` — avrebbe
 * scritto `1½` per un `1.7`, cioe' un numero falso. Quello riconosce il mezzo
 * ESATTO e per tutto il resto ricade su `decimale()`, col separatore giusto
 * nelle otto lingue.
 */
import { puntiTorneo } from '../../utils/tournament-helpers';
import './ReturnLegCountdown.css';

interface ReturnLegCountdownProps {
  /** Epoch ms at which the return leg starts (server-authoritative). */
  startsAt: number;
  /** Match points from this player's point of view. */
  score: { you: number; opponent: number } | null;
  /** This player already pressed "pronto". */
  ready: boolean;
  /** The opponent already pressed "pronto". */
  opponentReady: boolean;
  onReady: () => void;
  /** Leave the game — forfeits the return leg. Omitted in the compact variant. */
  onLeave?: () => void;
  /**
   * Whether this match counts for the rating AND defers it to the end.
   *
   * Only then is the deferred-rating note shown — promising an update that will
   * never arrive would be worse than silence. Two different things can make it
   * false: an amichevole, and a match on a FREE opening, where each leg is rated
   * the moment it ends and the note would be plainly untrue.
   */
  rated?: boolean;
  /** Compact variant for the board overlay (no buttons). */
  compact?: boolean;
}

/**
 * End of the FIRST leg of a two-leg match ("andata e ritorno"): the return game
 * starts by itself, so instead of a rematch panel the player gets a countdown,
 * the running match score, and a warning that leaving now forfeits the return.
 * "Pronto" only skips the wait — it is not consent, the leg starts either way.
 */
export default function ReturnLegCountdown({
  startsAt,
  score,
  ready,
  opponentReady,
  onReady,
  onLeave,
  rated = false,
  compact = false,
}: ReturnLegCountdownProps) {
  const { t } = useTranslation();
  const [seconds, setSeconds] = useState(() => Math.max(0, Math.ceil((startsAt - Date.now()) / 1000)));

  useEffect(() => {
    const tick = () => setSeconds(Math.max(0, Math.ceil((startsAt - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [startsAt]);

  const countdownText = t('online.match.returnStartsIn', { seconds });

  if (compact) {
    return (
      <div className="return-leg-pill" role="status">
        <Repeat2 size={16} aria-hidden="true" />
        <span>{countdownText}</span>
      </div>
    );
  }

  return (
    <div className="return-leg-countdown" data-testid="return-leg-countdown">
      <div className="rlc-leg">{t('online.match.legOf', { leg: 2, total: 2 })}</div>

      {score && (
        <div className="rlc-score">
          <span className="rlc-score-label">{t('online.match.score')}</span>
          <span className="rlc-score-value">
            {puntiTorneo(score.you)} - {puntiTorneo(score.opponent)}
          </span>
        </div>
      )}

      <p className="rlc-countdown" role="status">{countdownText}</p>
      <p className="rlc-warning">{t('online.match.leavingForfeits')}</p>
      {/* Both games are rated together at the end of the match, so nothing moved
          when leg 1 ended. Without a word it reads as a bug. */}
      {rated && <p className="rlc-rating-note">{t('online.match.ratingAtMatchEnd')}</p>}

      <button
        type="button"
        className="rlc-ready-btn"
        onClick={onReady}
        disabled={ready}
        data-testid="return-leg-ready"
      >
        {ready ? <Check size={20} aria-hidden="true" /> : <Repeat2 size={20} aria-hidden="true" />}
        <span>{ready ? t('online.match.waitingOpponent') : t('online.match.ready')}</span>
      </button>

      {opponentReady && !ready && (
        <p className="rlc-opponent-ready">{t('online.match.opponentReady')}</p>
      )}

      {/* The warning above says leaving forfeits the return leg — so there has to
          be a way to actually do it, deliberately de-emphasised. */}
      {onLeave && (
        <button type="button" className="rlc-leave-btn" onClick={onLeave} data-testid="return-leg-leave">
          {t('online.rematch.newOpponent')}
        </button>
      )}
    </div>
  );
}
